// Library Methods
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

// Constants
import { fixationLossMethodEnums } from "src/constants/enums";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

const useExamReportStats = (examReport: ExamSectionReportType | undefined) => {
  const { t } = useTranslation();

  const falsePositivePercentage = useMemo(() => {
    if (examReport?.stats?.falsePositivePercentage === undefined) return "-";

    return !isNaN(examReport?.stats?.falsePositivePercentage)
      ? `${Math.floor(examReport?.stats?.falsePositivePercentage * 100)}%`
      : "-";
  }, [examReport?.stats?.falsePositivePercentage]);

  const falseNegative = useMemo(() => {
    if (
      examReport?.stats?.falseNegativeMisses === undefined ||
      examReport?.stats?.falseNegativeCount === undefined
    )
      return "-";

    return !isNaN(examReport?.stats?.falseNegativeMisses) &&
      !isNaN(examReport?.stats?.falseNegativeCount)
      ? `${examReport?.stats?.falseNegativeMisses}/${examReport?.stats?.falseNegativeCount}`
      : "-";
  }, [
    examReport?.stats?.falseNegativeCount,
    examReport?.stats?.falseNegativeMisses,
  ]);

  const fixationLossPercentage = useMemo(() => {
    if (
      examReport?.stats?.fixationLossPercentage === undefined ||
      typeof examReport?.stats?.fixationLossPercentage === "string"
    )
      return "-";

    return examReport?.stats?.fixationLossMethod === "BlindSpot"
      ? `${examReport?.stats?.fixationLossNonFixatedCount}/${examReport?.stats?.fixationLossTotalCount}`
      : !isNaN(examReport?.stats?.fixationLossPercentage)
        ? `${Math.floor(examReport?.stats?.fixationLossPercentage * 100)}%`
        : "-";
  }, [
    examReport?.stats?.fixationLossMethod,
    examReport?.stats?.fixationLossNonFixatedCount,
    examReport?.stats?.fixationLossPercentage,
    examReport?.stats?.fixationLossTotalCount,
  ]);

  const fixationLossMethod = useMemo(
    () =>
      examReport?.stats?.fixationLossMethod ===
      fixationLossMethodEnums.BLIND_SPOT
        ? t("Blind Spot")
        : t("Eye Tracking"),
    [examReport?.stats?.fixationLossMethod, t]
  );

  return {
    fixationLossMethod,
    fixationLossPercentage,
    falsePositivePercentage,
    falseNegative,
  };
};

export default useExamReportStats;
